import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import { Alert, Col, Input, InputNumber, Row, Select, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { Check, CircleAlert, Package, TriangleAlert } from 'lucide-react';

const { Text } = Typography;
const bp = '/bp';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });
const CHECK_LABELS = {
  bulk_exists: 'Bulk batch exists', bulk_is_manufacturing: 'Bulk is a manufacturing batch', sfg_present: 'AQL-accepted units (SFG)',
  bulk_status: 'Bulk batch completed / released', bulk_release_testing: 'Bulk release testing (sterility)', units_remaining: 'Units remaining',
  recipe_is_packaging: 'Packaging recipe', pack_config: 'Pack configuration (MPR)', same_product: 'Same product', mbpr_release_gate: 'MBPR release gate',
  no_over_reservation: 'Within remaining units', theoretical_packs: 'Theoretical packs',
};

/**
 * Bulk batch picker for packaging (BPR) issuance — BP3 / BP4 / BP12.
 * Lists the bulk batches that carry AQL-accepted units, reads `batch/<bulk>/packaging-readiness/` for the
 * selected one and reports the checks, accepted / reserved / remaining units and the theoretical packs.
 * `onChange` receives {bulk_batch_number, bulk_units, at_risk, at_risk_reason, change_control_ref, theoretical_packs, readiness}.
 */
export default function BulkBatchPicker({ recipeId, value, onChange, market, compact = false }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [units, setUnits] = useState(null);
  const [atRisk, setAtRisk] = useState(false);
  const [reason, setReason] = useState('');
  const [cc, setCc] = useState('');

  useEffect(() => {
    if (!recipeId) { setCandidates([]); return; }
    setLoading(true);
    Axios.get(`${bp}/packaging/bulk-candidates/`, { params: { recipe_id: recipeId }, ...auth() })
      .then((res) => setCandidates(res?.data?.data || []))
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
  }, [recipeId]);

  const load = useCallback(async (bulk, u, r, c) => {
    if (!bulk || !recipeId) { setReadiness(null); return null; }
    try {
      const res = await Axios.get(`${bp}/batch/${encodeURIComponent(bulk)}/packaging-readiness/`, {
        params: { recipe_id: recipeId, units: u || undefined, at_risk_reason: r || undefined, change_control_ref: c || undefined, market: market || undefined }, ...auth(),
      });
      const d = res?.data?.data || null;
      setReadiness(d);
      return d;
    } catch (e) {
      const d = e?.response?.data?.data || null;
      setReadiness(d);
      return d;
    }
  }, [recipeId, market]);

  const emit = useCallback((bulk, d, u, r, c, risk) => {
    if (!onChange) return;
    onChange(bulk ? {
      bulk_batch_number: bulk, bulk_units: u || (d ? d.remaining_units : null), at_risk: risk, at_risk_reason: risk ? r : '', change_control_ref: risk ? c : '',
      theoretical_packs: d ? d.theoretical_packs : null, readiness: d,
    } : null);
  }, [onChange]);

  // re-read whenever any input changes
  useEffect(() => {
    let alive = true;
    (async () => {
      const d = await load(value, units, atRisk ? reason : '', atRisk ? cc : '');
      if (alive) emit(value, d, units, reason, cc, atRisk);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, units, atRisk, reason, cc, recipeId, market]);

  const options = useMemo(() => candidates.map((c) => ({
    value: c.batch_number,
    label: `${c.batch_number} — ${c.status} · accepted ${c.accepted_units} · remaining ${c.remaining_units}${c.packaging_batches?.length ? ` (packs: ${c.packaging_batches.join(', ')})` : ''}`,
  })), [candidates]);

  const soft = (readiness?.checks || []).filter((c) => !c.ok && c.overridable);
  const hard = (readiness?.checks || []).filter((c) => !c.ok && c.blocking && !c.overridable);
  const span = compact ? 12 : 8;

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <Space style={{ marginBottom: 8 }}><Package size={16} /><Text strong>Bulk batch to pack</Text>
        <Tooltip title="A packaging batch is issued from the AQL-accepted units of a released (or QA-conditionally released) bulk batch. Theoretical packs = units ÷ units per pack, rounded down (BP3 / BP4).">
          <span className="text-gray-400 text-xs">ⓘ</span></Tooltip></Space>
      <Row gutter={[16, 8]}>
        <Col xs={24} lg={span * 2}>
          <Select style={{ width: '100%' }} showSearch allowClear loading={loading} placeholder={recipeId ? 'Select the bulk batch with accepted units' : 'Select a packaging recipe first'}
            value={value || undefined} options={options} disabled={!recipeId} optionFilterProp="label"
            onChange={(v) => { setUnits(null); onChange && onChange(v ? { bulk_batch_number: v, bulk_units: null, at_risk: atRisk, at_risk_reason: '', change_control_ref: '', theoretical_packs: null, readiness: null } : null); }} />
        </Col>
        <Col xs={24} lg={span}>
          <InputNumber style={{ width: '100%' }} min={1} max={readiness?.remaining_units || undefined} value={units ?? readiness?.remaining_units ?? null}
            onChange={(v) => setUnits(v || null)} placeholder="units to pack" disabled={!value} addonAfter="units" />
        </Col>
      </Row>
      {readiness && (
        <div style={{ marginTop: 10 }}>
          <Space wrap size={[6, 6]}>
            <Tag color="geekblue">accepted {readiness.accepted_units}</Tag>
            <Tag color={readiness.reserved_units ? 'orange' : 'default'}>reserved {readiness.reserved_units}{readiness.reserved?.length ? ` (${readiness.reserved.map((r) => r.batch_number).join(', ')})` : ''}</Tag>
            <Tag color="green">remaining {readiness.remaining_units}</Tag>
            {readiness.units_per_pack != null && <Tag>{readiness.units_per_pack} unit(s) / pack{readiness.packs_per_shipper ? ` · ${readiness.packs_per_shipper} packs / shipper` : ''}</Tag>}
            <Tag color="purple"><b>{readiness.theoretical_packs}</b> theoretical packs{readiness.expected_shippers != null ? ` · ${readiness.expected_shippers} shippers` : ''}</Tag>
          </Space>
          <div style={{ marginTop: 8 }}>
            <Space wrap size={[6, 6]}>
              {(readiness.checks || []).map((c) => (
                <Tooltip key={c.key} title={c.reason || CHECK_LABELS[c.key] || c.key}>
                  <Tag icon={c.ok ? <Check size={12} style={{ verticalAlign: -2 }} /> : c.overridable ? <TriangleAlert size={12} style={{ verticalAlign: -2 }} /> : <CircleAlert size={12} style={{ verticalAlign: -2 }} />}
                    color={c.ok ? 'success' : c.overridable ? 'warning' : c.blocking ? 'error' : 'default'}>{' '}{CHECK_LABELS[c.key] || c.key}</Tag>
                </Tooltip>))}
            </Space>
          </div>
          {hard.length > 0 && <Alert type="error" showIcon style={{ marginTop: 8 }} message="Packaging issuance blocked" description={hard.map((c) => c.reason).join(' · ')} />}
          {soft.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Alert type="warning" showIcon message={<span>Issue at risk (QA conditional release) <Switch size="small" checked={atRisk} onChange={setAtRisk} style={{ marginLeft: 8 }} /></span>}
                description={soft.map((c) => c.reason).join(' · ')} />
              {atRisk && (
                <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                  <Col xs={24} lg={14}><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for issuing at risk (mandatory)" /></Col>
                  <Col xs={24} lg={10}><Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Change-control ref. (mandatory)" /></Col>
                </Row>)}
            </div>)}
          {readiness.ok && <Alert type={readiness.at_risk ? 'warning' : 'success'} showIcon style={{ marginTop: 8 }}
            message={readiness.at_risk ? `Ready to issue AT RISK — ${readiness.theoretical_packs} packs under ${cc}` : `Ready to issue — ${readiness.theoretical_packs} packs from ${readiness.requested_units} accepted units`} />}
          {readiness.artwork?.length > 0 && <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>Artwork set{market ? ` (${market})` : ''}: {readiness.artwork.map((a) => `${a.artwork_no} v${a.version}`).join(', ')}</Text>}
        </div>)}
    </div>
  );
}
